import { useState, useEffect } from "react";
import { IoMdCheckmark } from "react-icons/io";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../styles/__labDialogAddEditTest.module.scss";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { toast } from "../toast";
import {
  addImagingTechnician,
  getPermittedTechnicianGenders,
  updateImagingTechnician,
} from "../../slice/imagingTechnicians/imagingTechniciansSlice";

// Canonical "Male" / "Female" for stored values in any casing; anything else is
// returned as-is so a legacy value can still be shown in edit mode.
const normalizeGender = (value) => {
  const v = String(value || "").trim();
  if (v.toLowerCase() === "male") return "Male";
  if (v.toLowerCase() === "female") return "Female";
  return v;
};

// The country code is picked separately, so the local number must not start with
// the trunk "0": leading zeros (and any spaces before them) are dropped while typing
// or pasting, and when an existing number is loaded for editing.
const stripLeadingZeros = (value) => String(value || "").replace(/^[\s0]+/, "");

export default function LabDialogAddEditTechnical({
  open,
  onClose,
  type = "add",
  technician = null,
}) {
  const [name, setName] = useState("");
  const [gender, setGender] = useState("Male");
  const [status, setStatus] = useState(true);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isSaving } = useSelector((state) => state.imagingTechnicians);
  const [selectedCountry, setSelectedCountry] = useState("+964");
  const [mobileNumber, setMobileNumber] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  // Genders the admin assigned to this imaging center ("Gender Preference");
  // null while loading.
  const [permittedGenders, setPermittedGenders] = useState(null);

  // Technician's stored gender when editing. It stays selectable-as-is even if the
  // admin has since narrowed the center's preference, so editing other details never
  // silently changes it; it just can't be picked again once changed.
  const originalGender =
    type === "edit" && technician ? normalizeGender(technician.gender) : "";

  // Re-read the center's permitted genders every time the dialog opens so a change
  // made in the Admin Panel is reflected without logging out.
  useEffect(() => {
    if (!open) {
      setPermittedGenders(null);
      return undefined;
    }
    let cancelled = false;
    getPermittedTechnicianGenders().then((genders) => {
      if (!cancelled) setPermittedGenders(genders);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Keep the selection on a permitted gender: default a new technician (or one with
  // no stored gender) to the first permitted option.
  useEffect(() => {
    if (!open || !permittedGenders) return;
    const keepsOriginal = !!originalGender && gender === originalGender;
    if (!permittedGenders.includes(gender) && !keepsOriginal) {
      setGender(permittedGenders[0]);
    }
  }, [open, permittedGenders, gender, originalGender]);

  // Country dial codes + ISO (for flagcdn flags), same list/pattern as the
  // nurse "Add Nurse" and lab "Add Technician" dialogs for a uniform experience.
  const countries = [
    { code: "+964", iso: "iq", name: "Iraq" },
    { code: "+91", iso: "in", name: "India" },
    { code: "+966", iso: "sa", name: "Saudi Arabia" },
    { code: "+971", iso: "ae", name: "UAE" },
    { code: "+965", iso: "kw", name: "Kuwait" },
    { code: "+974", iso: "qa", name: "Qatar" },
    { code: "+973", iso: "bh", name: "Bahrain" },
    { code: "+968", iso: "om", name: "Oman" },
    { code: "+962", iso: "jo", name: "Jordan" },
    { code: "+961", iso: "lb", name: "Lebanon" },
    { code: "+20", iso: "eg", name: "Egypt" },
    { code: "+90", iso: "tr", name: "Turkey" },
    { code: "+98", iso: "ir", name: "Iran" },
    { code: "+92", iso: "pk", name: "Pakistan" },
    { code: "+880", iso: "bd", name: "Bangladesh" },
    { code: "+44", iso: "gb", name: "United Kingdom" },
    { code: "+1", iso: "us", name: "United States" },
    { code: "+49", iso: "de", name: "Germany" },
    { code: "+33", iso: "fr", name: "France" },
    { code: "+61", iso: "au", name: "Australia" },
    { code: "+86", iso: "cn", name: "China" },
    { code: "+81", iso: "jp", name: "Japan" },
    { code: "+82", iso: "kr", name: "South Korea" },
    { code: "+60", iso: "my", name: "Malaysia" },
    { code: "+63", iso: "ph", name: "Philippines" },
    { code: "+234", iso: "ng", name: "Nigeria" },
    { code: "+27", iso: "za", name: "South Africa" },
    { code: "+254", iso: "ke", name: "Kenya" },
    { code: "+55", iso: "br", name: "Brazil" },
    { code: "+52", iso: "mx", name: "Mexico" },
  ];

  // The dropdown lists countries A–Z by name.
  const sortedCountries = [...countries].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const selectedCountryData =
    countries.find((c) => c.code === selectedCountry) || countries[0];

  // Close the country dropdown when clicking outside it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isCountryDropdownOpen &&
        !e.target.closest("[data-country-dropdown]")
      ) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCountryDropdownOpen]);

  // Populate form when editing
  useEffect(() => {
    if (type === "edit" && technician) {
      setName(technician.name || "");
      setGender(normalizeGender(technician.gender));
      setStatus(technician.status === "ACTIVE");
      // Parse mobile number: extract country code and number (longest code first
      // so e.g. "+964" wins over "+9"/"+1").
      const mobile = (technician.mobileNumber || "").trim();
      const sorted = [...countries].sort(
        (a, b) => b.code.length - a.code.length,
      );
      const matchedCountry = sorted.find((c) => mobile.startsWith(c.code));
      if (matchedCountry) {
        setSelectedCountry(matchedCountry.code);
        setMobileNumber(
          stripLeadingZeros(
            mobile.substring(matchedCountry.code.length).replace(/[\s-]/g, ""),
          ),
        );
      } else {
        setMobileNumber(stripLeadingZeros(mobile.replace(/^\+/, "")));
      }
    } else if (type === "add") {
      setName("");
      setGender("Male");
      setStatus(true);
      setSelectedCountry("+964");
      setMobileNumber("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, technician, open]);

  useEffect(() => {
    if (open) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter technician name");
      return;
    }
    if (!mobileNumber.trim()) {
      toast.error("Please enter mobile number");
      return;
    }
    // Permitted genders still loading (Save is disabled meanwhile).
    if (!permittedGenders) return;
    if (
      !permittedGenders.includes(gender) &&
      !(originalGender && gender === originalGender)
    ) {
      toast.error("Please select a gender permitted for your imaging center");
      return;
    }

    const fullMobile = `${selectedCountry} ${mobileNumber.trim()}`;

    if (type === "add") {
      const result = await dispatch(
        addImagingTechnician({
          name: name.trim(),
          gender,
          mobileNumber: fullMobile,
        }),
      );
      if (!result.type.endsWith("/rejected")) onClose(true);
    } else {
      const result = await dispatch(
        updateImagingTechnician({
          technicianId: technician.id,
          data: {
            name: name.trim(),
            gender,
            mobileNumber: fullMobile,
            status: status ? "ACTIVE" : "INACTIVE",
          },
        }),
      );
      if (!result.type.endsWith("/rejected")) onClose(true);
    }
  };

  return (
    <div className={`${styles["labDialog_wrapper"]}`}>
      <div className={`${styles["dialog-overlay"]}`}>
        <div className={styles["dialog-box"]}>
          <button
            className={styles["dialog-close"]}
            onClick={() => onClose(false)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z"
                fill="currentcolor"
              />
            </svg>
          </button>

          <div className={styles["mb-24"]}>
            <h2 className={styles["dialog-title"]}>
              {type === "add" ? "Add Technician" : "Edit Technician Details"}
            </h2>
          </div>

          <div className={styles["form-design"]}>
            <div className={styles["form-group"]}>
              <label>Name</label>
              <input
                type="text"
                placeholder="Enter technician name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={styles["text-input"]}
              />
            </div>

            <div className={styles["form-group"]}>
              <label>Gender</label>
              {/* Only the genders the admin assigned to this center are offered. */}
              <select
                className={styles["select-input"]}
                value={permittedGenders ? gender : ""}
                onChange={(e) => setGender(e.target.value)}
                disabled={!permittedGenders}
                aria-busy={!permittedGenders}
              >
                {!permittedGenders && <option value="">Loading...</option>}
                {permittedGenders &&
                  originalGender &&
                  !permittedGenders.includes(originalGender) && (
                    <option value={originalGender} disabled>
                      {originalGender} (not permitted)
                    </option>
                  )}
                {(permittedGenders || []).map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles["form-group"]}>
              <label htmlFor="mobile">Mobile Number</label>
              <div style={{ position: "relative" }} data-country-dropdown>
                <div className={styles["mobile-input-container"]}>
                  <div
                    className={styles["country-selector"]}
                    onClick={() =>
                      setIsCountryDropdownOpen(!isCountryDropdownOpen)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <span className={styles["selected-flag"]}>
                      <img
                        src={`https://flagcdn.com/24x18/${selectedCountryData?.iso}.png`}
                        alt={selectedCountryData?.name}
                        style={{
                          width: "24px",
                          height: "18px",
                          borderRadius: "2px",
                          objectFit: "cover",
                        }}
                      />
                      <span className={styles["selected-countryCode"]}>
                        {selectedCountryData.code}
                      </span>
                    </span>
                    <ChevronDown
                      size={14}
                      className={styles["dropdown-arrow"]}
                    />
                  </div>
                  <input
                    type="tel"
                    id="mobile"
                    placeholder="770 123 4567"
                    value={mobileNumber}
                    onChange={(e) =>
                      setMobileNumber(
                        stripLeadingZeros(e.target.value.replace(/\D/g, "")),
                      )
                    }
                    className={styles["phone-input"]}
                  />
                </div>

                {/* Custom dropdown panel - rendered OUTSIDE the input container
                    so it isn't clipped by `overflow: hidden`. */}
                {isCountryDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: "240px",
                      maxHeight: "220px",
                      overflowY: "auto",
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      zIndex: 1100,
                      marginTop: "4px",
                    }}
                  >
                    {sortedCountries.map((country) => (
                      <div
                        key={country.code}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCountry(country.code);
                          setIsCountryDropdownOpen(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontSize: "13px",
                          backgroundColor:
                            selectedCountry === country.code
                              ? "#e6f7f7"
                              : "#fff",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f3f4f6")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            selectedCountry === country.code
                              ? "#e6f7f7"
                              : "#fff")
                        }
                      >
                        <img
                          src={`https://flagcdn.com/24x18/${country.iso}.png`}
                          alt={country.name}
                          style={{
                            width: "24px",
                            height: "18px",
                            borderRadius: "2px",
                            objectFit: "cover",
                          }}
                        />
                        <span style={{ fontWeight: "500" }}>
                          {country.code}
                        </span>
                        <span style={{ color: "#6b7280" }}>{country.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Status (Active/Inactive) toggle hidden by product request.
                The `status` state is retained (defaults to ACTIVE) so the
                save payload still sends a valid status to the backend. */}
          </div>

          <div className={`${styles["dialog-footer"]}`}>
            <button
              className={`${styles["btn-outlined-1"]}`}
              onClick={() => onClose(false)}
            >
              Cancel
            </button>
            <button
              className={styles["btn-1"]}
              onClick={handleSave}
              disabled={isSaving || !permittedGenders}
            >
              <IoMdCheckmark /> {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
